import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Plus, Search } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar';
// Direct module imports, not the `@/features/assistant` barrel: the sidebar is
// in every authed layout, and the barrel would drag the whole assistant
// feature (chat UI, cards, AI SDK) into the shared chunk.
import { useConversations } from '@/features/assistant/api/use-conversations';
import { useSearchConversations } from '@/features/assistant/api/use-search-conversations';
import {
  formatCompactAge,
  groupByRecency,
} from '@/features/assistant/lib/conversation-recency';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MAX_LENGTH = 200;
const UNTITLED = 'New conversation';

/**
 * The sidebar body in "Chat" mode (ENG-1161): Claire's chat history.
 *
 *   [ Search chat history…   ] [+]
 *   TODAY / PREVIOUS 7 DAYS / PREVIOUS 30 DAYS / OLDER   (collapsible groups)
 *     Title …                                  3h
 *
 * With a search term the grouped list is replaced by server-side results
 * (`GET /assistant/conversations/search`), which match message TEXT as well as
 * titles; each hit shows a snippet of the matching message under its title.
 *
 * Rows are links to `/assistant?id=…`; "+" goes to `/assistant?new=1`, the same
 * new-chat entry point the Conversations page uses.
 */
export function NavChatHistory({ onNavigate }: { onNavigate?: () => void }) {
  const [search, setSearch] = useState('');
  const searchTerm = search.trim();
  const query = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const activeId = useRouterState({
    select: (s) =>
      s.location.pathname === '/assistant'
        ? (s.location.search as { id?: string }).id
        : undefined,
  });

  return (
    <>
      {/*
        Sticky: SidebarContent is the scroll container, so the search box and
        "+" stay reachable however long the history gets.
      */}
      <div className="bg-sidebar sticky top-0 z-10 flex items-center gap-1.5 px-2 pt-2 pb-1">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
          />
          <SidebarInput
            aria-label="Search chat history"
            className="pl-7"
            onChange={(event) =>
              setSearch(event.target.value.slice(0, SEARCH_MAX_LENGTH))
            }
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSearch('');
            }}
            maxLength={SEARCH_MAX_LENGTH}
            placeholder="Search chats…"
            type="search"
            value={search}
          />
        </div>
        <Button
          aria-label="New chat"
          asChild
          className="size-8 shrink-0"
          size="icon"
          title="New chat"
          variant="outline"
        >
          <Link onClick={onNavigate} search={{ new: '1' }} to="/assistant">
            <Plus />
          </Link>
        </Button>
      </div>

      {searchTerm ? (
        <ChatSearchResults
          activeId={activeId}
          isDebouncing={searchTerm !== query}
          onNavigate={onNavigate}
          query={query}
        />
      ) : (
        <ChatHistoryGroups activeId={activeId} onNavigate={onNavigate} />
      )}
    </>
  );
}

interface ListProps {
  activeId: string | undefined;
  onNavigate?: () => void;
}

function ChatHistoryGroups({ activeId, onNavigate }: ListProps) {
  const { conversations, isLoading, isError } = useConversations();

  if (isLoading) return <HistorySkeleton />;
  if (isError) {
    return <HistoryMessage>Couldn&rsquo;t load your chats.</HistoryMessage>;
  }
  if (conversations.length === 0) {
    return (
      <HistoryMessage>
        No chats yet. Start one with the + button.
      </HistoryMessage>
    );
  }

  const now = new Date();
  return (
    <>
      {groupByRecency(conversations, now).map((group) => (
        <Collapsible className="group/collapsible" defaultOpen key={group.key}>
          <SidebarGroup className="py-1">
            <SidebarGroupLabel
              asChild
              className="text-sidebar-foreground/60 hover:text-sidebar-foreground text-[11px] font-semibold tracking-wide uppercase"
            >
              <CollapsibleTrigger>
                <ChevronRight className="mr-1 size-3! transition-transform group-data-[state=open]/collapsible:rotate-90" />
                {group.label}
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu aria-label={`${group.label} chats`}>
                  {group.items.map((conversation) => (
                    <SidebarMenuItem key={conversation.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={conversation.id === activeId}
                      >
                        <Link
                          onClick={onNavigate}
                          search={{ id: conversation.id }}
                          to="/assistant"
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {conversation.title || UNTITLED}
                          </span>
                          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                            {formatCompactAge(conversation.updatedAt, now)}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      ))}
    </>
  );
}

function ChatSearchResults({
  query,
  isDebouncing,
  activeId,
  onNavigate,
}: ListProps & { query: string; isDebouncing: boolean }) {
  const { results, isLoading, isSearching, isError } = useSearchConversations({
    q: query,
  });

  if (isLoading || ((isDebouncing || isSearching) && results.length === 0)) {
    return <HistorySkeleton />;
  }
  if (isError) {
    return <HistoryMessage>Search failed. Try again.</HistoryMessage>;
  }
  if (results.length === 0) {
    return (
      <HistoryMessage>No chats match &ldquo;{query}&rdquo;.</HistoryMessage>
    );
  }

  const now = new Date();
  return (
    <SidebarGroup className="py-1">
      <SidebarGroupLabel className="text-sidebar-foreground/60 text-[11px] font-semibold tracking-wide uppercase">
        Results
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu aria-label="Chat search results">
          {results.map((result) => (
            <SidebarMenuItem key={result.id}>
              <SidebarMenuButton
                asChild
                className="h-auto items-start py-1.5"
                isActive={result.id === activeId}
              >
                <Link
                  onClick={onNavigate}
                  search={{ id: result.id }}
                  to="/assistant"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{result.title || UNTITLED}</span>
                    {result.snippet ? (
                      <span className="text-muted-foreground truncate text-xs">
                        {result.snippet}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-muted-foreground shrink-0 pt-0.5 text-xs tabular-nums">
                    {formatCompactAge(result.updatedAt, now)}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function HistorySkeleton() {
  return (
    <SidebarGroup aria-busy="true" aria-label="Loading chats">
      <SidebarMenu>
        {Array.from({ length: 5 }).map((_, index) => (
          <SidebarMenuItem key={index}>
            <SidebarMenuSkeleton />
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function HistoryMessage({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground px-4 py-6 text-center text-xs">
      {children}
    </p>
  );
}
